<template>
	<app-title-bar :icon="['fas', 'fa-folder']" subtitle="Product Data" :title="category?.name">
		<template #icon>
			<product-category-icon :category="id"></product-category-icon>
		</template>
		<product-type-form v-if="canEdit" :category="id" @completed="load" />

		<app-button to="/product-data/product-categories" class="ml-2" icon="fa-circle-left">
			Back
		</app-button>
	</app-title-bar>

	<!-- Render only when data is ready -->
	<template v-if="!loading && category">
		<app-panel title="Documentation" class="mt-5">
			<template #header>
				<add-document v-if="canEdit" :product-category="category.id" @completed="load" />
			</template>
			<documents-table
				:documents="tableDocuments"
				:products="categoryProducts"
				:readonly="!canEdit"
				:show-business-unit-column="false"
				@document-removed="load"
				@refresh-documents="load" />
		</app-panel>

		<div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
			<app-panel
				v-for="type in category.types"
				:key="type.id"
				class="mt-5"
				:title="type.name">
				<template #header>
					<product-type-actions v-if="canEdit" :product-type="type" @completed="load" />
				</template>

				<router-link
					v-for="product in type.products"
					:key="product.id"
					:to="`/product-data/products/${product.id}`"
					class="block text-blue-600 hover:underline">
					{{ product.name }}
				</router-link>
			</app-panel>
		</div>
	</template>

	<!-- Loading skeleton -->
	<div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
		<div v-for="n in 4" :key="n" class="bg-white rounded shadow p-4 animate-pulse">
			<div class="h-6 w-1/3 bg-gray-300 rounded mb-4"></div>
			<div class="space-y-2">
				<div v-for="i in 3" :key="i" class="h-5 w-3/4 bg-gray-200 rounded"></div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { onMounted, ref, watch, computed } from 'vue'
	import { useRoute } from 'vue-router'
	import AppPanel from '@/components/layout/panel/AppPanel.vue'
	import ProductCategoryService from '@/services/product-category'
	import ProductCategoryIcon from '@/components/partials/icons/ProductCategoryIcon.vue'
	import ProductTypeForm from '@/components/partials/product-data/ProductTypeForm.vue'
	import ProductTypeActions from '@/components/partials/product-data/ProductTypeActions.vue'
	import type { ProductCategory } from '@/data/schema/product-category'
	import type { Product } from '@/data/schema/product'
	import type { Document } from '@/data/schema/document'
	import useAuthStore from '@/stores/auth'
	import AddDocument from '@/components/partials/documents/AddDocument.vue'
	import DocumentsTable from '@/components/datatables/all/DocumentsTable.vue'

	function resolveId(param: unknown): string | null {
		if (typeof param === 'string') return param
		if (Array.isArray(param)) return param[0]
		return null
	}

	const category = ref<ProductCategory | null>(null)
	const tableDocuments = ref<Document[]>([])
	const loading = ref(true)
	const route = useRoute()
	const authStore = useAuthStore()
	const canEdit = computed(() => authStore.isAbleTo(['edit-product-range']))

	const id = ref<string | null>(resolveId(route.params.id))

	let loadInProgress = false

	const categoryProducts = computed(() => {
		if (!category.value) return []
		return category.value.types?.flatMap((type) => type.products || []) || []
	})

	const load = async () => {
		if (!id.value) return
		if (loadInProgress) return // stops infinite loops

		loadInProgress = true
		loading.value = true

		try {
			const api = new ProductCategoryService()
			const response = await api.get(id.value, {
				expand: [
					'types',
					'types.products',
					'documents',
					'documents.type',
					'documents.language',
					'documents.revisions',
					'documents.revisions.file'
				]
			})

			category.value = response.data ?? null
			tableDocuments.value = response.data?.documents ?? []
		} catch (error) {
			console.error('Error fetching product category:', error)
			category.value = null
		} finally {
			loading.value = false
			loadInProgress = false
		}
	}

	// eslint-disable-next-line no-unused-vars
	const _productCreated = (value: Product | undefined | false) => {
		if (value) load()
	}

	onMounted(() => {
		if (id.value) load()
	})

	watch(
		() => route.params.id,
		(newId) => {
			const resolved = resolveId(newId)
			if (resolved === id.value) return
			id.value = resolved
			if (id.value) load()
		}
	)
</script>
